import { useCallback } from 'react';
import { Message, MessageAttachment, MessageMention, MessageStatus } from '@connect-x-app/shared-domain-models';
import { mapMessageToSendSocketMessage, mapStringToMessage } from '@connect-x-app/shared-utils';
import { ChatMessage } from '@connect-x-app/shared-view-models';
import { v4 as uuidv4 } from 'uuid';

import { useChannelSessionStore, useFlightRoomSessionStore, useMessageSessionStore, useMessageStore } from '../stores';
import { useUploadAttachment } from './queries';
import { useChatMessageSocketSendEvents } from './socketEvents/useChatMessageSocketSendEvents';
import { useAuthentication } from './useAuthentication';

export const useSendChatMessage = () => {
  const uploadAttachment = useUploadAttachment();
  const { user } = useAuthentication();
  const { addMessage: addMessageToStore, removeMessage } = useMessageStore();
  const { hasLoadedLastPage } = useMessageSessionStore();
  const { sendChatMessage } = useChatMessageSocketSendEvents();
  const { selectedChannelId } = useChannelSessionStore();
  const { selectedFlightRoomId } = useFlightRoomSessionStore();

  const addMessage = useCallback(
    (message: Message, status: MessageStatus) => {
      message = { ...message, status };
      addMessageToStore({
        addToMostRecentChunk: hasLoadedLastPage,
        message: message,
      });
    },
    [addMessageToStore, hasLoadedLastPage],
  );

  const send = async (
    messageText: string,
    mentions: MessageMention[] | null,
    attachmentFile?: File | null,
    clientMessageId = uuidv4(),
  ): Promise<boolean> => {
    if (!selectedChannelId || !selectedFlightRoomId) {
      return false;
    }
    const message = mapStringToMessage(
      selectedFlightRoomId,
      selectedChannelId,
      user!.id || user!.externalId,
      clientMessageId,
      messageText,
      mentions,
      attachmentFile,
    );
    addMessage(message, MessageStatus.PENDING);

    let attachments: MessageAttachment[] = [];
    if (attachmentFile) {
      try {
        const uploadedAttachment = await uploadAttachment(
          attachmentFile,
          selectedChannelId,
          selectedFlightRoomId,
          clientMessageId,
        );
        attachments = [
          {
            id: uploadedAttachment.id,
            name: uploadedAttachment.name,
            type: uploadedAttachment.fileType,
          },
        ];
      } catch {
        addMessage(message, MessageStatus.FAILED);
        return false;
      }
    }

    const socketMessage = mapMessageToSendSocketMessage(message, attachments);
    try {
      const response = await sendChatMessage(socketMessage);
      if (response && !response.ok) {
        addMessage(message, MessageStatus.FAILED);
        return false;
      }
    } catch {
      addMessage(message, MessageStatus.FAILED);
      return false;
    }

    return true;
  };

  const remove = (message: ChatMessage) => {
    if (!selectedChannelId || !selectedFlightRoomId) {
      return;
    }

    removeMessage({
      channelId: selectedChannelId,
      messageId: message.externalId ?? message.id,
    });
  };

  const retry = async (message: ChatMessage, pendingImage?: File | null): Promise<boolean> => {
    // A failed message never received a server id, so it is normally still keyed by its
    // original client id (externalId). Reuse it so the retry reconciles with the existing
    // entry in place and the server echo matches the original send.
    if (message.externalId) {
      return await send(message.text, message.mentions, pendingImage, message.externalId);
    }
    // Without a client id the entry is unusable: it is keyed by an empty string (colliding
    // with every other never-sent message) and the backend rejects an empty clientMessageId.
    // Drop the stale entry and resend with a fresh client id.
    remove(message);
    return await send(message.text, message.mentions, pendingImage);
  };

  return { remove, retry, send };
};
